import React, { type HTMLAttributes } from 'react'

import { arrayMove, findItemIndexAtPosition, getScrollableParent } from './helpers'
import { useDrag, useDropTarget } from './hooks'
import type { Point } from './types'

const DEFAULT_CONTAINER_TAG = 'div'

type Props<TTag extends keyof JSX.IntrinsicElements> = HTMLAttributes<TTag> & {
  children: React.ReactNode
  /** Determines whether drag functionality is enabled, defaults to true */
  allowDrag?: boolean
  /** Called when the user starts a sorting gesture. */
  onSortStart?: () => void
  /** Called when the position of an item changed during a sorting gesture. */
  onSortMove?: (newIndex: number) => void
  /** Called when the user finishes a sorting gesture. */
  onSortEnd: (oldIndex: number, newIndex: number) => void
  /** Class applied to the item being dragged */
  draggedItemClassName?: string
  /** Determines which type of html tag will be used for a container element */
  as?: TTag
  /** Determines if an axis should be locked */
  lockAxis?: 'x' | 'y'
  /** Reference to the Custom Holder element */
  customHolderRef?: React.RefObject<HTMLElement | null>
  /** Drop target to be used when dragging */
  dropTarget?: React.ReactNode
  /** Scroll the window or parent element when dragging */
  autoScroll?: boolean
}

// this context is only used so that SortableItems can register/remove themselves
// from the items list
type Context = {
  registerItem: (item: HTMLElement) => void
  removeItem: (item: HTMLElement) => void
  registerKnob: (item: HTMLElement) => void
  removeKnob: (item: HTMLElement) => void
}

const SortableListContext = React.createContext<Context | undefined>(undefined)
const SortableList = <TTag extends keyof JSX.IntrinsicElements = typeof DEFAULT_CONTAINER_TAG>({
  children,
  allowDrag = true,
  onSortStart,
  onSortMove,
  onSortEnd,
  draggedItemClassName,
  as,
  lockAxis,
  customHolderRef,
  dropTarget,
  autoScroll = false,
  ...rest
}: Props<TTag>) => {
  // this array contains the elements than can be sorted (wrapped inside SortableItem)
  const itemsRef = React.useRef<HTMLElement[]>([])
  // this array contains the coordinates of each sortable element (only computed on dragStart and used in dragMove for perf reason)
  const itemsRect = React.useRef<DOMRect[]>([])
  // Hold all registered knobs
  const knobs = React.useRef<HTMLElement[]>([])
  // contains the container element
  const containerRef = React.useRef<HTMLElement | null>(null)
  // contains the target element (copy of the source element)
  const targetRef = React.useRef<HTMLElement | null>(null)
  // contains the index in the itemsRef array of the element being dragged
  const sourceIndexRef = React.useRef<number | undefined>(undefined)
  // contains the index in the itemsRef of the element to be exchanged with the source item
  const lastTargetIndexRef = React.useRef<number | undefined>(undefined)
  // contains the offset point where the initial drag occurred to be used when dragging the item
  const offsetPointRef = React.useRef<Point>({ x: 0, y: 0 })
  // contains the dropTarget logic
  const dropTargetLogic = useDropTarget(dropTarget)
  // contains the original opacity of the sorted item in order te restore it correctly
  const sourceOpacityRef = React.useRef<string>('1')
  // contains the speed at which the container scrolls horizontally or vertically when auto scrolling
  const scrollSpeedRef = React.useRef<Point>({ x: 0, y: 0 })
  // contains the auto scroll animation
  const scrollAnimationRef = React.useRef<number | null>(null)
  // contains the scrollable list parent (element or window)
  const scrollContainerRef = React.useRef<HTMLElement | Window | null>(null)
  // contains the horizontal and vertical scroll positions of the container
  const initialScrollRef = React.useRef<Point>({ x: 0, y: 0 })

  // auto scroll method
  const autoScrolling = React.useCallback(() => {
    const scroller = scrollContainerRef.current
    const scrollSpeed = scrollSpeedRef.current

    if ((scrollSpeed.x === 0 && scrollSpeed.y === 0) || !scroller) {
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current)
        scrollAnimationRef.current = null
      }
      return
    }

    // handle both window and element scrolling
    if (scroller instanceof Window) {
      scroller.scrollBy(scrollSpeed.x, scrollSpeed.y)
    } else if (scroller instanceof HTMLElement) {
      scroller.scrollTop += scrollSpeed.y
      scroller.scrollLeft += scrollSpeed.x
    }

    scrollAnimationRef.current = requestAnimationFrame(autoScrolling)
  }, [])

  React.useEffect(() => {
    const holder = customHolderRef?.current || document.body
    return () => {
      // cleanup the target element from the DOM when SortableList in unmounted
      if (targetRef.current) {
        holder.removeChild(targetRef.current)
      }
    }
  }, [customHolderRef])

  const updateTargetPosition = (position: Point) => {
    if (targetRef.current && sourceIndexRef.current !== undefined) {
      const offset = offsetPointRef.current
      const sourceRect = itemsRect.current[sourceIndexRef.current]
      const newX = lockAxis === 'y' ? sourceRect.left : position.x - offset.x
      const newY = lockAxis === 'x' ? sourceRect.top : position.y - offset.y

      // we use `translate3d` to force using the GPU if available
      targetRef.current.style.transform = `translate3d(${newX}px, ${newY}px, 0px)`
    }
  }

  const copyItem = React.useCallback(
    (sourceIndex: number) => {
      if (!containerRef.current) {
        return
      }

      const source = itemsRef.current[sourceIndex]
      const sourceRect = itemsRect.current[sourceIndex]

      const copy = source.cloneNode(true) as HTMLElement

      // added the "dragged" class name
      if (draggedItemClassName) {
        draggedItemClassName.split(' ').forEach((c) => copy.classList.add(c))
      }

      // we ensure the copy has the same size than the source element
      copy.style.width = `${sourceRect.width}px`
      copy.style.height = `${sourceRect.height}px`
      // we place the target starting position to the top left of the window
      // it will then be moved relatively using `transform: translate3d()`
      copy.style.position = 'fixed'
      copy.style.margin = '0'
      copy.style.top = '0'
      copy.style.left = '0'

      const sourceCanvases = source.querySelectorAll('canvas')
      copy.querySelectorAll('canvas').forEach((canvas, index) => {
        canvas.getContext('2d')?.drawImage(sourceCanvases[index], 0, 0)
      })

      const holder = customHolderRef?.current || document.body
      holder.appendChild(copy)

      targetRef.current = copy
    },
    [customHolderRef, draggedItemClassName]
  )

  const listeners = useDrag({
    allowDrag,
    containerRef,
    knobs: knobs.current,
    onStart: ({ pointInWindow }) => {
      if (!containerRef.current) {
        return
      }

      // auto scrolling of the container
      if (autoScroll) {
        const scroller = getScrollableParent(containerRef.current)
        scrollContainerRef.current = scroller

        // record the starting scroll position to calculate the scroll delta later
        // record the starting scroll position for both axes
        if (scroller instanceof HTMLElement) {
          initialScrollRef.current = { y: scroller.scrollTop, x: scroller.scrollLeft }
        } else {
          initialScrollRef.current = { y: scroller.scrollY, x: scroller.scrollX }
        }
      }

      itemsRect.current = itemsRef.current.map((item) => item.getBoundingClientRect())

      const sourceIndex = findItemIndexAtPosition(pointInWindow, itemsRect.current)
      // if we are not starting the drag gesture on a SortableItem, we exit early
      if (sourceIndex === -1) {
        return
      }

      // saving the index of the item being dragged
      sourceIndexRef.current = sourceIndex

      // let the parent know that sort started
      if (onSortStart) {
        onSortStart()
      }

      // the item being dragged is copied to the document body and will be used as the target
      copyItem(sourceIndex)

      // hide source during the drag gesture (and store original opacity)
      const source = itemsRef.current[sourceIndex]
      sourceOpacityRef.current = source.style.opacity ?? '1'
      source.style.opacity = '0'
      source.style.visibility = 'hidden'

      // get the offset between the source item's window position relative to the point in window
      const sourceRect = source.getBoundingClientRect()
      offsetPointRef.current = {
        x: pointInWindow.x - sourceRect.left,
        y: pointInWindow.y - sourceRect.top,
      }

      // set the initial position of the cloned item
      updateTargetPosition(pointInWindow)
      dropTargetLogic.show?.(sourceRect)

      // Adds a nice little physical feedback
      if (window.navigator.vibrate) {
        window.navigator.vibrate(100)
      }
    },
    onMove: ({ pointInWindow }) => {
      if (!containerRef.current) {
        return
      }

      updateTargetPosition(pointInWindow)

      const sourceIndex = sourceIndexRef.current
      // if there is no source, we exit early (happened when drag gesture was started outside a SortableItem)
      if (sourceIndex === undefined) {
        return
      }

      if (autoScroll) {
        const scroller = scrollContainerRef.current
        if (scroller) {
          // auto scroll trigger logic point
          const SCROLL_THRESHOLD = 60
          const MAX_SCROLL_SPEED = 15
          const { x: pointerX, y: pointerY } = pointInWindow

          // reset speed before recalculating
          scrollSpeedRef.current = { x: 0, y: 0 }

          let scrollerRect
          if (scroller instanceof Window) {
            scrollerRect = {
              top: 0,
              bottom: scroller.innerHeight,
              left: 0,
              right: scroller.innerWidth,
            }
          } else {
            scrollerRect = scroller.getBoundingClientRect()
          }

          // vertical scroll logic (if not locked)
          if (lockAxis !== 'x') {
            if (pointerY < scrollerRect.top + SCROLL_THRESHOLD && pointerY >= scrollerRect.top) {
              const proximity = scrollerRect.top + SCROLL_THRESHOLD - pointerY
              scrollSpeedRef.current.y = -MAX_SCROLL_SPEED * (proximity / SCROLL_THRESHOLD)
            } else if (
              pointerY > scrollerRect.bottom - SCROLL_THRESHOLD &&
              pointerY <= scrollerRect.bottom
            ) {
              const proximity = pointerY - (scrollerRect.bottom - SCROLL_THRESHOLD)
              scrollSpeedRef.current.y = MAX_SCROLL_SPEED * (proximity / SCROLL_THRESHOLD)
            }
          }

          // horizontal scroll logic (if not locked)
          if (lockAxis !== 'y') {
            if (pointerX < scrollerRect.left + SCROLL_THRESHOLD && pointerX >= scrollerRect.left) {
              const proximity = scrollerRect.left + SCROLL_THRESHOLD - pointerX
              scrollSpeedRef.current.x = -MAX_SCROLL_SPEED * (proximity / SCROLL_THRESHOLD)
            } else if (
              pointerX > scrollerRect.right - SCROLL_THRESHOLD &&
              pointerX <= scrollerRect.right
            ) {
              const proximity = pointerX - (scrollerRect.right - SCROLL_THRESHOLD)
              scrollSpeedRef.current.x = MAX_SCROLL_SPEED * (proximity / SCROLL_THRESHOLD)
            }
          }

          // start animation if speed is not 0
          if (
            (scrollSpeedRef.current.x !== 0 || scrollSpeedRef.current.y !== 0) &&
            !scrollAnimationRef.current
          ) {
            scrollAnimationRef.current = requestAnimationFrame(autoScrolling)
          }
        }
      }

      // drop-point correction for both axes
      let scrollDelta = { x: 0, y: 0 }
      if (autoScroll && scrollContainerRef.current) {
        const scroller = scrollContainerRef.current
        if (scroller instanceof HTMLElement) {
          scrollDelta = {
            y: scroller.scrollTop - initialScrollRef.current.y,
            x: scroller.scrollLeft - initialScrollRef.current.x,
          }
        } else if (scroller instanceof Window) {
          scrollDelta = {
            y: scroller.scrollY - initialScrollRef.current.y,
            x: scroller.scrollX - initialScrollRef.current.x,
          }
        }
      }

      const sourceRect = itemsRect.current[sourceIndex]
      const targetPoint: Point = {
        x: (lockAxis === 'y' ? sourceRect.left : pointInWindow.x) + scrollDelta.x,
        y: (lockAxis === 'x' ? sourceRect.top : pointInWindow.y) + scrollDelta.y,
      }

      const targetIndex = findItemIndexAtPosition(targetPoint, itemsRect.current, {
        fallbackToClosest: true,
      })
      // if not target detected, we don't need to update other items' position
      if (targetIndex === -1) {
        return
      }

      // if targetIndex changed and last target index is set we can let the parent know the new position
      if (
        onSortMove &&
        lastTargetIndexRef.current !== undefined &&
        lastTargetIndexRef.current !== targetIndex
      ) {
        onSortMove(targetIndex)
      }

      // we keep track of the last target index (to be passed to the onSortEnd callback)
      lastTargetIndexRef.current = targetIndex

      const isMovingRight = sourceIndex < targetIndex

      // in this loop, we go over each sortable item and see if we need to update their position
      for (let index = 0; index < itemsRef.current.length; index += 1) {
        const currentItem = itemsRef.current[index]
        const currentItemRect = itemsRect.current[index]
        // if current index is between sourceIndex and targetIndex, we need to translate them
        if (
          (isMovingRight && index >= sourceIndex && index <= targetIndex) ||
          (!isMovingRight && index >= targetIndex && index <= sourceIndex)
        ) {
          // we need to move the item to the previous or next item position
          const nextItemRects = itemsRect.current[isMovingRight ? index - 1 : index + 1]
          if (nextItemRects) {
            const translateX = nextItemRects.left - currentItemRect.left
            const translateY = nextItemRects.top - currentItemRect.top
            // we use `translate3d` to force using the GPU if available
            currentItem.style.transform = `translate3d(${translateX}px, ${translateY}px, 0px)`
          }
        }
        // otherwise, the item should be at its original position
        else {
          currentItem.style.transform = 'translate3d(0,0,0)'
        }
        // we want the translation to be animated
        currentItem.style.transitionDuration = '300ms'
      }

      dropTargetLogic.setPosition?.(lastTargetIndexRef.current, itemsRect.current, lockAxis)
    },
    onEnd: () => {
      // reset auto scroll variables
      if (autoScroll) {
        // reset the ref that holds the scrollable container
        scrollContainerRef.current = null
        // reset the scroll speed
        scrollSpeedRef.current = { x: 0, y: 0 }
        // cancel any ongoing animation frame loop
        if (scrollAnimationRef.current) {
          cancelAnimationFrame(scrollAnimationRef.current)
          scrollAnimationRef.current = null
        }
      }

      // we reset all items translations (the parent is expected to sort the items in the onSortEnd callback)
      for (let index = 0; index < itemsRef.current.length; index += 1) {
        const currentItem = itemsRef.current[index]
        currentItem.style.transform = ''
        currentItem.style.transitionDuration = ''
      }

      const sourceIndex = sourceIndexRef.current
      if (sourceIndex !== undefined) {
        // show the source item again
        const source = itemsRef.current[sourceIndex]
        if (source) {
          source.style.opacity = sourceOpacityRef.current
          source.style.visibility = ''
        }

        const targetIndex = lastTargetIndexRef.current
        if (targetIndex !== undefined) {
          if (sourceIndex !== targetIndex) {
            // sort our internal items array
            itemsRef.current = arrayMove(itemsRef.current, sourceIndex, targetIndex)
            // let the parent know
            onSortEnd(sourceIndex, targetIndex)
          }
        }
      }

      // reset internal state refs
      sourceIndexRef.current = undefined
      lastTargetIndexRef.current = undefined
      dropTargetLogic.hide?.()

      // cleanup the target element from the DOM
      if (targetRef.current) {
        const holder = customHolderRef?.current || document.body
        holder.removeChild(targetRef.current)
        targetRef.current = null
      }
    },
  })

  const registerItem = React.useCallback((item: HTMLElement) => {
    itemsRef.current.push(item)
  }, [])

  const removeItem = React.useCallback((item: HTMLElement) => {
    const index = itemsRef.current.indexOf(item)
    if (index !== -1) {
      itemsRef.current.splice(index, 1)
    }
  }, [])

  const registerKnob = React.useCallback((item: HTMLElement) => {
    knobs.current.push(item)
  }, [])

  const removeKnob = React.useCallback((item: HTMLElement) => {
    const index = knobs.current.indexOf(item)

    if (index !== -1) {
      knobs.current.splice(index, 1)
    }
  }, [])

  // we need to memoize the context to avoid re-rendering every children of the context provider
  // when not needed
  const context = React.useMemo(
    () => ({ registerItem, removeItem, registerKnob, removeKnob }),
    [registerItem, removeItem, registerKnob, removeKnob]
  )

  return React.createElement(
    as || DEFAULT_CONTAINER_TAG,
    {
      ...(allowDrag ? listeners : {}),
      ...rest,
      ref: containerRef,
    },
    <SortableListContext.Provider value={context}>
      {children}
      {dropTargetLogic.render?.()}
    </SortableListContext.Provider>
  )
}

export default SortableList

type ItemProps = {
  children: React.ReactElement
}

/**
 * SortableItem only adds a ref to its children so that we can register it to the main Sortable
 */
export const SortableItem = ({ children }: ItemProps) => {
  const context = React.useContext(SortableListContext)
  if (!context) {
    throw new Error('SortableItem must be a child of SortableList')
  }
  const { registerItem, removeItem } = context
  const elementRef = React.useRef<HTMLElement | null>(null)

  React.useEffect(() => {
    const currentItem = elementRef.current
    if (currentItem) {
      registerItem(currentItem)
    }

    return () => {
      if (currentItem) {
        removeItem(currentItem)
      }
    }
    // if the children changes, we want to re-register the DOM node
  }, [registerItem, removeItem, children])

  return React.cloneElement(children, { ref: elementRef })
}

export const SortableKnob = ({ children }: ItemProps) => {
  const context = React.useContext(SortableListContext)

  if (!context) {
    throw new Error('SortableKnob must be a child of SortableList')
  }

  const { registerKnob, removeKnob } = context

  const elementRef = React.useRef<HTMLElement | null>(null)

  React.useEffect(() => {
    const currentItem = elementRef.current

    if (currentItem) {
      registerKnob(currentItem)
    }

    return () => {
      if (currentItem) {
        removeKnob(currentItem)
      }
    }
    // if the children changes, we want to re-register the DOM node
  }, [registerKnob, removeKnob, children])

  return React.cloneElement(children, { ref: elementRef })
}
